// 8th from - Refrences & Testimonials
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Edit, Loader2, Plus, ShieldAlert, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Upload,
  Trash2,
  FileText,
  Image as ImageIcon,
  Image,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { registerSupplierAction } from '../../../reduxstore/actions/authActions';
import { LockClosedIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import CustomerReferenceModal from '@/js/components/modals/CustomerReferenceModal';
import { MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import TestimonialModal from '@/js/components/modals/TestimonialModal';

const additionalDetailsSchema = z.object({
  who_are_your_major_customers: z.array(z.string()).optional(),
});

const CustomerReferenceCard = ({ reference, onEdit, onDelete, index }) => {
  return (
    <div className="flex flex-col h-[277px]">
      {' '}
      {/* Set fixed height */}
      <div className="flex justify-between items-center bg-darkPurple rounded-t-lg">
        <div className="text-white px-2 py-1 rounded text-sm font-medium">
          Reference {index + 1}#
        </div>
        <div className="flex space-x-2 pr-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              onEdit(reference, index);
            }}
            className="text-white"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(index)} className="text-white">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="border border-gray-200 rounded-b-lg px-2 py-2 flex-1 overflow-hidden">
        <div className="space-y-2 text-sm h-full">
          <div>
            <span className="font-medium text-gray-700">Name</span>
            <div className="text-gray-500 truncate">{reference?.name}</div>
          </div>

          <div>
            <span className="font-medium text-gray-700">Email</span>
            <div className="text-gray-500 truncate">{reference?.email}</div>
          </div>

          <div>
            <span className="font-medium text-gray-700">Website</span>
            <div className="text-gray-500 truncate">
              {reference.website ? reference.website : 'Add website'}
            </div>
          </div>

          <div>
            <span className="font-medium text-gray-700">LinkedIn</span>
            <div className="text-gray-500 truncate">
              {reference.linkedin ? reference.linkedin : 'Add LinkedIn'}
            </div>
          </div>

          <div>
            <span className="font-medium text-gray-700">Phone</span>
            <div className="text-gray-500 truncate">
              {reference?.phone ? reference?.phone : 'Add Phone number'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TestimonialsTable = ({ testimonials, onAdd, onEdit, onDelete }) => {
  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        Add Testimonials
      </div>
      <div className="w-full border border-gray-300 rounded-md overflow-hidden">
        <div className="grid grid-cols-[30%,70%] bg-veryVeryLightPurple py-2 px-4 font-medium text-sm text-gray-800">
          <div>Name</div>
          <div>Testimonial</div>
        </div>
        {testimonials.map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-[30%,70%] items-center py-2 px-4 border-t border-gray-200 bg-gray-50 text-sm relative"
          >
            <div className="truncate pr-2">{item.name || '—'}</div>
            <div className="truncate pr-2">{item.text || '—'}</div>
            <div className="text-right absolute right-0 top-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEdit(item, index);
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDelete(index);
                    }}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
        {/* {testimonials.length === 0 && (
          <div className="grid grid-cols-3 py-2 px-4 text-sm text-gray-400 bg-gray-50">
            <div className="col-span-2">Enter Name</div>
            <div>Text of Testimonial</div>
          </div>
        )} */}
      </div>

      <Button
        variant="outline"
        className="py-1 px-2 flex gap-1 items-center text-sm text-darkPurple border-darkPurple hover:text-darkerPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAdd();
        }}
      >
        <Plus className="h-8 w-8" />
        Add
      </Button>
    </div>
  );
};

const SuccessStoriesTable = ({ stories, onAdd, onEdit, onDelete }) => {
  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        Add Success Stories
      </div>
      <div className="w-full border border-gray-300 rounded-md overflow-hidden">
        <div className="grid grid-cols-[30%,70%] bg-veryVeryLightPurple py-2 px-4 font-medium text-sm text-gray-800">
          <div>Name</div>
          <div>Success Story</div>
        </div>

        {stories.map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-[30%,70%] items-center py-2 px-4 border-t border-gray-200 bg-gray-50 text-sm relative"
          >
            <div className="truncate pr-2">{item.name || '—'}</div>
            <div className="truncate pr-2">{item.text || '—'}</div>
            <div className="text-right absolute right-0 top-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEdit(item, index);
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDelete(index);
                    }}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        className="py-1 px-2 flex gap-1 items-center text-sm text-darkPurple border-darkPurple hover:text-darkerPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAdd();
        }}
      >
        <Plus className="h-8 w-8" />
        Add
      </Button>
    </div>
  );
};

const RefrencesTestimonialsScreen = ({ setSectionId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);
  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [majorCustomers, setMajorCustomers] = useState(
    Array.isArray(reduxData?.who_are_your_major_customers)
      ? reduxData.who_are_your_major_customers
      : reduxData?.who_are_your_major_customers?.split(',') || [],
  );

  const form = useForm({
    resolver: zodResolver(additionalDetailsSchema),
    defaultValues: {
      who_are_your_major_customers: reduxData?.who_are_your_major_customers
        ? majorCustomers
        : [],
    },
  });

  // Modals
  const [modals, setModals] = useState({
    customerReferences: false,
    testimonials: false,
    successStories: false,
  });

  const [editing, setEditing] = useState({
    customerReferences: null,
    testimonials: null,
    successStories: null,
  });

  const [customerReferences, setCustomerReferences] = useState(
    reduxData?.customer_references || [],
  );
  const [testimonials, setTestimonials] = useState(
    reduxData?.testimonials || [],
  );
  const [successStories, setSuccessStories] = useState(
    reduxData?.success_stories || [],
  );

  const handleAddOrEdit = (section, data, setter, currentList) => {
    if (editing[section] !== null) {
      const updated = [...currentList];
      updated[editing[section].index] = data;
      setter(updated);
    } else {
      setter([...currentList, data]);
    }
    setEditing((prev) => ({ ...prev, [section]: null }));
    setModals((prev) => ({ ...prev, [section]: false }));
  };

  const handleEdit = (section, item, index) => {
    setEditing((prev) => ({ ...prev, [section]: { ...item, index } }));
    setModals((prev) => ({ ...prev, [section]: true }));
  };

  const handleDelete = (section, index, setter, currentList) => {
    const updated = currentList.filter((_, i) => i !== index);
    setter(updated);
  };

  const closeModal = (section) => {
    setModals((prev) => ({ ...prev, [section]: false }));
    setEditing((prev) => ({ ...prev, [section]: null }));
  };

  const handleTagInput = (e, setter, fieldName) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      e.preventDefault();
      const value = e.target.value.trim();
      setter((prev) => [...new Set([...prev, value])]);
      e.target.value = '';
    }
  };

  const removeTag = (index, setter) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };
  const majorCustomersRef = useRef(null);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      // 1. Format Refrences & Testimonials data
      const formattedData = {
        major_customers: majorCustomers,
        customer_references: customerReferences,
        testimonials: testimonials,
        success_stories: successStories,
        references_document_ids: [],
      };

      // Submit form data
      const response = await apiClient.put(
        '/api/manufacturer-profile/references-testimonials-details',
        JSON.stringify(formattedData),
      );

      const result = response.data;

      if (!result.success) {
        setError(result.data.error_message || 'Form submission failed');
        throw new Error('Form submission failed: ' + result.message);
      }

      // 4. Update Redux and navigate
      const updatedData = {
        ...data,
        who_are_your_major_customers: majorCustomers,
        customer_references: customerReferences,
        testimonials: testimonials,
        success_stories: successStories,
      };

      console.log(updatedData);
      dispatch(registerSupplierAction(updatedData));

      setSectionId(6);
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.message || 'Form submission failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // FINAL RETURN STATEMENT
  return (
    <div className="w-full bg-white  px-20  rounded-md font-roboto text-gray-700">
      <h2 className="text-3xl font-semibold text-black text-center mb-4">
        References and Testimonials
      </h2>
      <p className="text-center text-[#637381] mb-8 text-base">
        Add references to build trust with buyers
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="who_are_your_major_customers"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Major Suppliers / Customers{' '}
                  <span className="text-gray-400 ">( Press enter to add )</span>
                </FormLabel>
                <FormControl>
                  <div>
                    <div className="relative">
                      <Input
                        placeholder="Mention your major suppliers or customers (1-5 names)"
                        onKeyDown={(e) =>
                          handleTagInput(
                            e,
                            setMajorCustomers,
                            'who_are_your_major_customers',
                          )
                        }
                        ref={majorCustomersRef}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = majorCustomersRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault: () => {},
                              },
                              setMajorCustomers,
                              'who_are_your_major_customers',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {majorCustomers.map((customer, index) => (
                        <div
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                          key={index}
                        >
                          {customer}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(
                                index,
                                setMajorCustomers,
                                'who_are_your_major_customers',
                              )
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <div className="">
            <h1 className="text-foreground mb-2 text-sm font-semibold text-gray-700">
              Add Customer References
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {customerReferences.map((reference, index) => (
                <CustomerReferenceCard
                  key={index}
                  reference={reference}
                  onEdit={(item, i) =>
                    handleEdit('customerReferences', item, i)
                  }
                  onDelete={(i) =>
                    handleDelete(
                      'customerReferences',
                      i,
                      setCustomerReferences,
                      customerReferences,
                    )
                  }
                  index={index}
                />
              ))}

              {/* Add Reference Button */}
              <div
                className="h-[277px] border-2 border-dashed border-gray-400 text-gray-500 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-darkerPurple hover:text-darkPurple transition-all ease-in duration-300"
                onClick={() =>
                  setModals((prev) => ({ ...prev, customerReferences: true }))
                }
              >
                <Plus className="h-8 w-8 mb-2" />
                <span className="font-medium">Add References</span>
              </div>
              <CustomerReferenceModal
                isOpen={modals.customerReferences}
                onClose={() => closeModal('customerReferences')}
                onSubmit={(data) =>
                  handleAddOrEdit(
                    'customerReferences',
                    data,
                    setCustomerReferences,
                    customerReferences,
                  )
                }
                editingReference={editing.customerReferences}
              />
            </div>
          </div>

          {/* Testimonials */}
          <TestimonialsTable
            testimonials={testimonials}
            onAdd={() => setModals((prev) => ({ ...prev, testimonials: true }))}
            onEdit={(item, index) => handleEdit('testimonials', item, index)}
            onDelete={(index) =>
              handleDelete('testimonials', index, setTestimonials, testimonials)
            }
          />

          <TestimonialModal
            isOpen={modals.testimonials}
            onClose={() => closeModal('testimonials')}
            onSubmit={(data) =>
              handleAddOrEdit(
                'testimonials',
                data,
                setTestimonials,
                testimonials,
              )
            }
            editingTestimonial={editing.testimonials}
          />

          {/* Success Stories */}
          <SuccessStoriesTable
            stories={successStories}
            onAdd={() =>
              setModals((prev) => ({ ...prev, successStories: true }))
            }
            onEdit={(item, index) => handleEdit('successStories', item, index)}
            onDelete={(index) =>
              handleDelete(
                'successStories',
                index,
                setSuccessStories,
                successStories,
              )
            }
          />

          <TestimonialModal
            type="successStories"
            isOpen={modals.successStories}
            onClose={() => closeModal('successStories')}
            onSubmit={(data) =>
              handleAddOrEdit(
                'successStories',
                data,
                setSuccessStories,
                successStories,
              )
            }
            editingTestimonial={editing.successStories}
          />

          <div className="flex justify-between mt-6 text-lg">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSectionId(4)}
              className="px-4 py-2 h-12 w-36 hover:bg-slate-200"
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 h-12 w-36 bg-darkPurple hover:bg-darkerPurple ease-in duration-300"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="font-roboto font-semibold text-base">
                    Saving...
                  </span>
                </>
              ) : (
                <span className="font-roboto font-semibold text-base">
                  Next
                </span>
              )}
            </Button>
          </div>

          <div className="flex flex-row items-center justify-center space-x-2 ">
            {/* conditional renderign if no error display this if error dispaly error instead */}
            {error ? (
              <>
                <ShieldAlert className="h-3 w-3" />
                <span className="text-xs text-red-600">{error}</span>
              </>
            ) : (
              <>
                <LockClosedIcon className="h-3 w-3" />
                <span className="text-xs text-[#2CD673]">
                  We use industry-standard encryption to protect your data
                </span>
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
};

export default RefrencesTestimonialsScreen;
